import * as React from "react";
import { useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

// The add/edit form for a single site (slice A6). Used for BOTH a new site and an existing one --
// never rendered until the user explicitly chooses Add Site or Edit (§8.3 requirement 4).
//
// Requirement 6: a new, unsaved site must be visually distinct from a persisted one. It is rendered
// on a tinted, outlined panel and labelled "New site", so the classic screen's core defect -- an
// unsaved placeholder that looked exactly like saved data -- cannot recur.
//
// Requirement 7: NO Delete here. Delete belongs only to persisted rows, in the read view.
export default function SiteEditorRow(props) {
  const site = props.site || null; // null => creating
  const onSave = props.onSave;
  const onCancel = props.onCancel;
  const saving = props.saving;

  const isNew = site == null;

  const [name, setName] = useState(isNew ? "" : site.facility ?? "");
  const [numFields, setNumFields] = useState(isNew ? "1" : String(site.numFields ?? 0));
  const [space, setSpace] = useState(isNew ? "" : site.space == null ? "" : String(site.space));
  const [indoor, setIndoor] = useState(isNew ? false : site.indooravailable === 1);
  const [outdoor, setOutdoor] = useState(isNew ? false : site.outdooravailable === 1);
  const [active, setActive] = useState(isNew ? true : site.facilityactive === 1);

  // No server-side check exists for this, so the dialog enforces it.
  const nameInvalid = name.trim() === "";

  const handleSave = () => {
    if (nameInvalid) return;
    onSave({
      facility_site_id: isNew ? 0 : site.facility_site_id,
      facilityName: name.trim(),
      facility_numFields: Number(numFields) || 0,
      facilitySpace: space.trim() === "" ? null : Number(space),
      facilityIndoorAvail: indoor ? 1 : 0,
      facilityOutdoorAvail: outdoor ? 1 : 0,
      facility_Active: active ? 1 : 0,
    });
  };

  return (
    <Box
      sx={{
        p: 1.5,
        mb: 1,
        borderRadius: 1,
        border: 1,
        borderColor: isNew ? "primary.main" : "divider",
        bgcolor: isNew ? "action.hover" : "background.paper",
      }}
    >
      <Typography variant="caption" color={isNew ? "primary.main" : "text.secondary"} fontWeight={600}>
        {isNew ? "New site — not yet saved" : `Editing: ${site.facility}`}
      </Typography>

      <Box sx={{ display: "flex", gap: 1.5, mt: 1, flexWrap: "wrap" }}>
        <TextField
          label="Site name"
          size="small"
          required
          value={name}
          onChange={(event) => setName(event.target.value)}
          error={nameInvalid}
          helperText={nameInvalid ? "Required" : " "}
          disabled={saving}
          sx={{ flex: "2 1 200px" }}
        />
        <TextField
          label="Fields"
          size="small"
          type="number"
          value={numFields}
          onChange={(event) => setNumFields(event.target.value)}
          disabled={saving}
          helperText=" "
          sx={{ flex: "0 1 100px" }}
        />
        <TextField
          label="Space"
          size="small"
          type="number"
          value={space}
          onChange={(event) => setSpace(event.target.value)}
          disabled={saving}
          helperText=" "
          sx={{ flex: "0 1 110px" }}
        />
      </Box>

      <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
        <FormControlLabel
          control={<Checkbox size="small" checked={indoor} onChange={(e) => setIndoor(e.target.checked)} disabled={saving} />}
          label={<Typography variant="body2">Indoor</Typography>}
        />
        <FormControlLabel
          control={<Checkbox size="small" checked={outdoor} onChange={(e) => setOutdoor(e.target.checked)} disabled={saving} />}
          label={<Typography variant="body2">Outdoor</Typography>}
        />
        <FormControlLabel
          control={<Checkbox size="small" checked={active} onChange={(e) => setActive(e.target.checked)} disabled={saving} />}
          label={<Typography variant="body2">Active</Typography>}
        />
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" onClick={onCancel} disabled={saving} color="inherit">
          Cancel
        </Button>
        <Button size="small" variant="contained" onClick={handleSave} disabled={saving || nameInvalid}>
          {saving ? "Saving…" : isNew ? "Add site" : "Save site"}
        </Button>
      </Box>
    </Box>
  );
}
